import type { OCAttachment } from '../types/gateway';

export interface AgentRoleContext {
  active_role: string;
  role_label: string;
  role_goal: string;
  expected_agent_behavior: string;
}

/**
 * Build params for a chat.send request. When `roleContext` is provided we
 * prepend a fenced metadata block to the message so the agent can adapt its
 * tone and outputs to the active UI role. The block is human-readable but
 * intentionally distinct so the agent (and any log scanners) can spot it.
 */
export function buildChatSend(
  sessionKey: string,
  message: string,
  attachments?: OCAttachment[],
  roleContext?: AgentRoleContext
) {
  const finalMessage = roleContext
    ? `[ROLE_CONTEXT]\n${JSON.stringify(roleContext)}\n[/ROLE_CONTEXT]\n\n${message}`
    : message;

  return {
    sessionKey,
    message: finalMessage,
    deliver: false,
    idempotencyKey: self.crypto?.randomUUID?.() || (Math.random().toString(36) + Date.now().toString(36)),
    attachments: attachments?.length ? attachments : undefined,
  };
}

/** Encode a file as an OpenClaw image attachment */
export async function fileToAttachment(file: File): Promise<OCAttachment> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const match = /^data:([^;]+);base64,(.+)$/.exec(dataUrl);
      if (match) {
        resolve({
          type: 'image',
          mimeType: match[1],
          content: match[2],
        });
      } else {
        reject(new Error('Failed to encode file as base64'));
      }
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ── Legacy exports (kept for any remaining imports) ─────────────────────────

/** @deprecated Use gateway.request('chat.send', buildChatSend(...)) */
export function buildMessageSend(content: string, sessionId?: string, attachments?: { name: string; type: string; data: string; size: number }[]) {
  // Convert old attachment format to new
  const ocAttachments: OCAttachment[] | undefined = attachments?.map((a) => ({
    type: 'image' as const,
    mimeType: a.type,
    content: a.data,
  }));
  return buildChatSend(sessionId || 'main', content, ocAttachments);
}

/** @deprecated Use buildChatSend directly */
export function buildSessionCreate(_name?: string) {
  return { type: 'req', method: 'session.create' };
}

/** @deprecated No longer used */
export function buildSessionList() {
  return { type: 'req', method: 'session.list' };
}

/** @deprecated No longer used */
export function buildPresenceUpdate(_status: string, _sessionId?: string) {
  return {};
}
